<script lang="ts" setup>
import { ref, watch, nextTick } from '#imports'
import { vOnClickOutside } from '@vueuse/components'

export interface UIDrawerProps {
    top?: number
    width?: number
    maxHeight?: number
    drawerAnchor?: 'left' | 'center' | 'right'
}

const props = withDefaults(defineProps<UIDrawerProps>(), {
    top: 22,
    width: 320,
    maxHeight: 500,
    drawerAnchor: 'center'
})

const emit = defineEmits(['close', 'open'])
defineSlots<{
    activator?: HTMLElement
    header?: any
    body: any
    footer?: any
}>()

const modelValue = defineModel<boolean>({ default: false })

// HTML elements
const drawer = ref<HTMLElement | null>(null)
const activator = ref<HTMLElement | null>(null)

// Positioning
const drawerPosition = ref<Record<string, string>>()
const transitionOriginPoint = ref('center center')

async function determineDrawerPosition() {
    await nextTick(() => {
        if (!activator.value || !drawer.value) {
            drawerPosition.value = undefined
            return
        }

        const { width: activatorWidth, x: activatorX } = activator.value.getBoundingClientRect()
        const WINDOW_PADDING = 16
        const WINDOW_WIDTH = document.documentElement.clientWidth

        // Calculate drawer width, respecting maximum allowed width
        const drawerWidth = Math.min(props.width!, WINDOW_WIDTH - 2 * WINDOW_PADDING)

        // Calculate the initial left position
        let left = {
            left: 0,
            center: -((drawerWidth / 2) - (activatorWidth / 2)),
            right: 0 - drawerWidth + activatorWidth,
        }[props.drawerAnchor!]

        // Adjust 'left' to prevent overflow
        const drawerLeftEdgePosition = activatorX + left
        const drawerRightEdgePosition = drawerLeftEdgePosition + drawerWidth
        let offset = left

        if (drawerLeftEdgePosition < WINDOW_PADDING) { // If the drawer is overflowing the left edge of the viewport
            offset = Number(left) + (WINDOW_PADDING - drawerLeftEdgePosition)
        }
        else if (drawerRightEdgePosition > WINDOW_WIDTH - WINDOW_PADDING) { // If the drawer is overflowing the right edge of the viewport
            offset = Number(left) - ((drawerRightEdgePosition + WINDOW_PADDING) - WINDOW_WIDTH)
        }

        left = offset

        // Set drawer position and intersection point
        const intersectionX = -offset + (props.drawerAnchor === 'right' ? activatorWidth : 0)
        transitionOriginPoint.value = `${intersectionX}px top`

        drawerPosition.value = {
            top: `${props.top}px`,
            left: `${left}px`,
            width: `${drawerWidth}px`,
        }
    })
}

async function open() {
    await determineDrawerPosition()
    if (modelValue.value) return

    modelValue.value = true

    emit('open')
}

function close() {
    if (!modelValue.value) return

    modelValue.value = false
    emit('close')
}

function onModelValueChange() {
    if (!modelValue.value) {
        close()

        return
    }

    open()
}

const onClickOutsideHandler = {
    handler: () => close(),
    options: { ignore: [activator] }
}

watch(modelValue, () => {
    onModelValueChange()
})
</script>

<template>
    <div class="drawer-container">
        <div ref="activator">
            <slot name="activator" />
        </div>
        <div>
            <Transition
                name="drawer"
                appear
            >
                <section
                    v-if="modelValue"
                    ref="drawer"
                    v-on-click-outside="[onClickOutsideHandler.handler, onClickOutsideHandler.options]"
                    class="drawer"
                    :class="{ 'drawer--has-header': $slots.header,
                              'drawer--has-footer': $slots.footer }"
                    :style="[
                        drawerPosition,
                        {
                            '--drawer-max-height': `min(90dvh, ${props.maxHeight}px)`,
                            'transformOrigin': transitionOriginPoint
                        }
                    ]"
                >
                    <header v-if="$slots.header" class="drawer__header">
                        <slot name="header" />
                    </header>

                    <main v-if="$slots.body" class="drawer__body">
                        <slot name="body" />
                    </main>

                    <footer v-if="$slots.footer" class="drawer__footer">
                        <slot name="footer" />
                    </footer>
                </section>
            </Transition>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.drawer {
    --animation-duration: 0.35s;
    --content-delay: 0.2s;
    --content-stagger: 0.1s;

    position: absolute;
    display: grid;
    grid-template-rows: minmax(0, 1fr); // Default: content only
    width: auto;
    max-height: var(--drawer-max-height);
    padding: 15px 0;
    background: var(--surface);
    color: var(--on-surface);
    border-radius: var(--border-radius);
    transform-origin: top right;
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.12),
        0 2px 4px rgba(0, 0, 0, 0.06);

    &--has-header:not(&--has-footer) {
        padding: 0 0 15px 0;
        grid-template-rows: auto minmax(0, 1fr);
    }

    &--has-footer:not(&--has-header) {
        padding: 15px 0 0 0;
        grid-template-rows: minmax(0, 1fr) auto;
    }

    &--has-header#{&}--has-footer {
        padding: 0;
        grid-template-rows: auto minmax(0, 1fr) auto;
    }

    &__header {
        display: flex;
        align-items: center;
        padding: 15px;
        min-height: fit-content;
    }

    &__body {
        padding: 0 15px 15px;
        overflow-y: auto;
        min-height: 0; // Required for scroll to work in grid
        scrollbar-width: none;

        &::-webkit-scrollbar {
            width: 0px;
        }
    }

    &__footer {
        padding: 15px;
        min-height: fit-content;
    }

    &-container {
        position: relative;
    }

    // Transition and animation
    &-enter-active,
    &-leave-active {
        transition: all var(--animation-duration) cubic-bezier(0.4, 0, 0.2, 1);
    }

    &-enter-from,
    &-leave-to {
        opacity: 0;
        transform: scale(0.2);
    }

    &-enter-to,
    &-leave-from {
        opacity: 1;
        transform: scale(1);
    }

    &__header,
    &__body,
    &__footer {
        opacity: 0;
        transform: translateY(15px);
        animation: slideUp var(--animation-duration) forwards;
    }

    &__header {
        animation-delay: var(--content-delay);
    }

    &__body {
        animation-delay: calc(var(--content-delay) + var(--content-stagger));
    }

    &__footer {
        animation-delay: calc(var(--content-delay) + var(--content-stagger) * 2);
    }

    @keyframes slideUp {
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
}
</style>
